// react
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES, DYNAMIC_ROUTES } from 'router';

// components
import { ActionsCategory } from '@karehero/llama/pwa/pages/ActionsCategory/ActionsCategory';
import { ModalPageTemplate } from '@karehero/llama/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { Typography } from '@mui/material';

// hooks
import { useActionPlan } from 'hooks/useActionPlan';
import { useCareCircle } from 'hooks';

// models
import { ActionPlanItem } from '@karehero/models';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';

export const ActionPlanCategory = () => {
  // hooks
  const { careCircleId } = useCareCircle();
  const { actionPlanCategories } = useActionPlan();
  const { actionPlanCategoryId } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { flags } = useFeatureFlags();

  // memos
  const actionPlanCategory = useMemo(
    () =>
      actionPlanCategories?.find(
        (category) => category.id === actionPlanCategoryId,
      ),
    [actionPlanCategories, actionPlanCategoryId],
  );

  const items = useMemo(() => {
    if (!actionPlanCategory) return [];

    let actionPlanItems = actionPlanCategory.actionPlanItems || [];

    // NOTE: If the `financialStatus` flag is not enabled, it updates a specific action plan item
    // with a new route URL. This is a temporary measure and will be removed once the financial
    // status is rolled out to all the users
    if (!flags.financialStatus) {
      actionPlanItems = actionPlanItems.map((action: ActionPlanItem) => {
        if (action.id === 'tell-us-about-your-loved-one-financial-status') {
          return {
            ...action,
            route:
              'https://share-eu1.hsforms.com/1UrPUPKQhQyy4FuCC5Y3vvg2b3nta',
          };
        }
        return action;
      });
    }

    return actionPlanItems.map((item: ActionPlanItem) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subTitle || '',
      isComplete: item.isComplete,
      onClick: () => {
        if (item.actionType === 'direct' && item.route) {
          if (item.route.startsWith('http')) {
            window.open(item.route, '_blank');
          } else {
            navigate(item.route);
          }
        } else {
          navigate(DYNAMIC_ROUTES.ACTION_PLAN_ITEM(item.id));
        }
      },
    }));
  }, [actionPlanCategory, flags.financialStatus, navigate]);

  const progress = useMemo(() => {
    if (!actionPlanCategory) return 0;
    const total = actionPlanCategory.totalItems || 0;
    const completed = actionPlanCategory.completedItems || 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }, [actionPlanCategory]);

  if (!careCircleId) {
    return (
      <ModalPageTemplate onBack={goBack}>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            p: 4,
          }}
        >
          Please ensure you have selected a care circle.
        </Typography>
      </ModalPageTemplate>
    );
  }

  if (!actionPlanCategory) return null;

  return (
    <ActionsCategory
      title={actionPlanCategory.title}
      description={actionPlanCategory.subTitle || ''}
      progress={progress}
      items={items}
      onBack={goBack}
    />
  );
};

export default ActionPlanCategory;
